<p-toast></p-toast>

<div class="expense-form-page p-fluid">
    <ng-container>
        <p-progressBar mode="indeterminate" class="page-progress" *ngIf="isLoading"></p-progressBar>


        <section class="page-toolbar">
            <div class="page-toolbar-copy">
                <h2>{{ isEditMode ? 'Edit Expense' : 'New Expense' }}</h2>
                <p>Capture the spend summary, accounting split, receipts, and approval-ready context in one place.</p>
            </div>
            <div class="page-toolbar-actions">
                <p-button label="Cancel" icon="pi pi-times" severity="secondary" [text]="true" (click)="discardExpense()"></p-button>
                <p-button label="Save Draft" icon="pi pi-save" [loading]="isActionLoading('save')" (click)="saveExpense()"></p-button>
                <p-button *ngIf="!canPostExpense || isEditMode" label="Submit for Approval" icon="pi pi-check-circle" severity="success" [loading]="isActionLoading('submit')" (click)="submitForApproval()"></p-button>
                <p-button *ngIf="canPostExpense && !isEditMode" label="Post Expense" icon="pi pi-check-circle" severity="success" [loading]="isActionLoading('post')" [disabled]="isLoading" (click)="postExpense()"></p-button>
            </div>
        </section>

        <section class="form-shell">
            <form [formGroup]="expenseForm" class="expense-form-grid">
                <app-page-form-section icon="pi pi-file-edit" title="Expense Overview" subtitle="Core reference, description, submission date, and status.">
                    <div class="section-grid">
                        <div class="field">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="pi pi-hashtag"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <input id="expenseNumber" pInputText formControlName="expenseNumber" readonly>
                                    <label for="expenseNumber">Expense #</label>
                                </p-iftaLabel>
                            </p-inputGroup>
                        </div>

                        <div class="field">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="pi pi-calendar"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <p-datePicker id="submissionDate" formControlName="submissionDate" dateFormat="yy-mm-dd" [readonlyInput]="true" styleClass="w-full"></p-datePicker>
                                    <label for="submissionDate">Expense Date *</label>
                                </p-iftaLabel>
                            </p-inputGroup>
                            <small class="p-error" *ngIf="hasError('submissionDate')">Expense date is required.</small>
                        </div>

                        <div class="field span-2">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="pi pi-book"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <input id="description" pInputText formControlName="description">
                                    <label for="description">Description *</label>
                                </p-iftaLabel>
                            </p-inputGroup>
                            <small class="p-error" *ngIf="hasError('description')">Description is required.</small>
                        </div>

                        <div class="field">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="pi pi-info-circle"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <input id="status" pInputText formControlName="status" readonly>
                                    <label for="status">Status</label>
                                </p-iftaLabel>
                            </p-inputGroup>
                        </div>
                    </div>
                </app-page-form-section>

                <app-page-form-section icon="pi pi-users" title="Parties" subtitle="Select a CRM client or any Party for this expense.">
                    <div class="section-grid">
                        <app-commercial-party-picker
                            class="span-2"
                            [owningCompanyId]="expenseOwningCompany?.id"
                            [value]="commercialPartyReference"
                            [roles]="expensePartyRoles"
                            partySource="PARTY"
                            [preservePartySelection]="true"
                            label="Expense Party"
                            (valueChange)="onCommercialPartyChange($event)">
                        </app-commercial-party-picker>
                    </div>
                </app-page-form-section>

                <app-page-form-section icon="pi pi-calculator" title="Financial Controls" subtitle="Currency, computed totals, and additional accounting notes.">
                    <div class="section-grid">
                        <div class="field">
                            <app-country-currency-select formControlName="currencyCode" [countryId]="null" [required]="true"></app-country-currency-select>
                            <small class="p-error" *ngIf="hasError('currencyCode')">Currency is required.</small>
                        </div>

                        <div class="field">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="pi pi-chart-line"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <p-inputNumber
                                        inputId="totalAmount"
                                        formControlName="totalAmount"
                                        mode="currency"
                                        [currency]="expenseForm.get('currencyCode')?.value || 'SAR'"
                                        locale="en-US"
                                        [readonly]="true"
                                        styleClass="w-full">
                                    </p-inputNumber>
                                    <label for="totalAmount">Total Amount</label>
                                </p-iftaLabel>
                            </p-inputGroup>
                        </div>

                        <div class="field">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="pi pi-arrow-up-circle"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <p-select inputId="creditAccountId" formControlName="creditAccountId"
                                        [options]="creditAccountOptions" optionLabel="label" optionValue="value"
                                        [filter]="true" filterBy="label" appendTo="body" styleClass="w-full"></p-select>
                                    <label for="creditAccountId">Credit COA Account *</label>
                                </p-iftaLabel>
                            </p-inputGroup>
                            <small class="p-error" *ngIf="hasError('creditAccountId')">Credit account is required.</small>
                        </div>

                        <div class="field">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="pi pi-briefcase"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <app-logistics-project-lookup inputId="logisticsProjectId" formControlName="logisticsProjectId"></app-logistics-project-lookup>
                                    <label for="logisticsProjectId">Logistics Project</label>
                                </p-iftaLabel>
                            </p-inputGroup>
                        </div>

                        <div class="field span-2">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="pi pi-pencil"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <textarea id="notes" pInputTextarea formControlName="notes" rows="3"></textarea>
                                    <label for="notes">Notes</label>
                                </p-iftaLabel>
                            </p-inputGroup>
                        </div>
                    </div>
                </app-page-form-section>

                <app-page-form-section icon="pi pi-list-check" title="Expense Items" subtitle="Split the expense into accounting lines, taxes, and reimbursement eligibility.">
                    <div class="items-toolbar">
                        <div>
                            <strong>{{ expenseItems.length }}</strong>
                            <span>line items</span>
                        </div>
                        <p-button label="Add Item" icon="pi pi-plus" severity="secondary" [outlined]="true" (click)="addExpenseItem()"></p-button>
                    </div>

                    <div class="items-table-wrapper" formArrayName="expenseItems">
                        <p-table [value]="expenseItems.controls" styleClass="p-datatable-sm expense-items-table" [tableStyle]="{ 'min-width': '104rem' }">
                            <ng-template pTemplate="header">
                                <tr>
                                    <th style="width: 3rem">#</th>
                                    <th style="width: 12rem">Date</th>
                                    <th>Description</th>
                                    <th style="width: 16rem">Account</th>
                                    <th style="width: 16rem">Project</th>
                                    <th style="width: 16rem">Vehicle</th>
                                    <th style="width: 10rem">Amount</th>
                                    <th style="width: 10rem">Tax</th>
                                    <th style="width: 8rem">Reimb.</th>
                                    <th style="width: 4rem"></th>
                                </tr>
                            </ng-template>

                            <ng-template pTemplate="body" let-item let-i="rowIndex">
                                <tr [formGroupName]="i">
                                    <td>{{ i + 1 }}</td>
                                    <td>
                                        <p-datePicker [inputId]="'itemDate_' + i" formControlName="itemDate" dateFormat="yy-mm-dd" appendTo="body" styleClass="w-full"></p-datePicker>
                                        <small class="p-error" *ngIf="hasItemError(i, 'itemDate')">Required</small>
                                    </td>
                                    <td>
                                        <input [id]="'itemDescription_' + i" pInputText formControlName="description" class="w-full">
                                        <small class="p-error" *ngIf="hasItemError(i, 'description')">Required</small>
                                    </td>
                                    <td>
                                        <p-select
                                            [inputId]="'itemAccount_' + i"
                                            [options]="chartOfAccounts"
                                            optionLabel="accountName"
                                            formControlName="account"
                                            placeholder="Select account"
                                            [filter]="true"
                                            filterBy="accountName"
                                            appendTo="body"
                                            styleClass="w-full">
                                        </p-select>
                                        <small class="p-error" *ngIf="hasItemError(i, 'account')">Required</small>
                                    </td>
                                    <td>
                                        <app-logistics-project-lookup [inputId]="'itemProject_' + i" formControlName="logisticsProjectId" placeholder="Header project"></app-logistics-project-lookup>
                                    </td>
                                    <td>
                                        <p-select
                                            [inputId]="'itemVehicle_' + i"
                                            [options]="vehicleOptions"
                                            optionLabel="label"
                                            optionValue="value"
                                            formControlName="vehicleId"
                                            placeholder="No vehicle"
                                            [filter]="true"
                                            filterBy="label"
                                            [showClear]="true"
                                            appendTo="body"
                                            styleClass="w-full">
                                        </p-select>
                                    </td>
                                    <td>
                                        <p-inputNumber [inputId]="'itemAmount_' + i" formControlName="amount" mode="decimal" [minFractionDigits]="2" [maxFractionDigits]="2" styleClass="w-full"></p-inputNumber>
                                        <small class="p-error" *ngIf="hasItemError(i, 'amount')">Required</small>
                                    </td>
                                    <td>
                                        <p-inputNumber [inputId]="'itemTaxAmount_' + i" formControlName="taxAmount" mode="decimal" [minFractionDigits]="2" [maxFractionDigits]="2" styleClass="w-full"></p-inputNumber>
                                    </td>
                                    <td class="text-center">
                                        <p-checkbox [inputId]="'isReimbursable_' + i" formControlName="isReimbursable" [binary]="true"></p-checkbox>
                                    </td>
                                    <td class="text-center">
                                        <button pButton pRipple type="button" icon="pi pi-trash" class="p-button-text p-button-danger" (click)="removeExpenseItem(i)"></button>
                                    </td>
                                </tr>
                            </ng-template>

                            <ng-template pTemplate="emptymessage">
                                <tr>
                                    <td colspan="9">
                                        <div class="empty-table-state">
                                            <i class="pi pi-list"></i>
                                            <span>No line items added yet.</span>
                                        </div>
                                    </td>
                                </tr>
                            </ng-template>
                        </p-table>
                    </div>
                </app-page-form-section>

                <app-page-form-section icon="pi pi-paperclip" title="Receipts & Attachments" subtitle="Attach supporting receipts or PDFs before approval.">
                    <p-fileUpload
                        mode="advanced"
                        [customUpload]="true"
                        [showUploadButton]="false"
                        [showCancelButton]="false"
                        [multiple]="true"
                        accept="image/*,application/pdf"
                        [maxFileSize]="1000000"
                        (onSelect)="onAttachmentsSelect($event)">
                        <ng-template pTemplate="empty">
                            <div class="upload-empty-state">
                                <i class="pi pi-cloud-upload"></i>
                                <span>Drag files here or browse to add receipts.</span>
                            </div>
                        </ng-template>
                    </p-fileUpload>

                    <div class="attachment-list" *ngIf="attachments.length > 0">
                        <article class="attachment-card" *ngFor="let attachment of attachments; let i = index">
                            <div class="attachment-meta">
                                <strong>{{ attachment.fileName }}</strong>
                                <span>{{ ((attachment.fileSize || 0) / 1024 / 1024).toFixed(2) }} MB</span>
                            </div>
                            <button pButton pRipple type="button" icon="pi pi-times" class="p-button-text p-button-danger" (click)="removeAttachment(i)"></button>
                        </article>
                    </div>
                </app-page-form-section>
            </form>
        </section>
    </ng-container>
</div>
